import { Flex, useToast } from "@chakra-ui/react";
import { Delete, Post } from "api";
import MultiInputEntries from "components/multiInputEntries";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useConfirmDialog } from "components/confirmDialog";

type GameChallengesRouteParams = {
  appId: string;
  partnerId: string;
};

interface GameChallengesType {
  appChallengeId: number;
  eventName: string;
  coins: string;
  showAsValue: boolean;
  originalTitle: string;
  originalUnit: string;
  threshold: string;
  isActive?: boolean;
  timeCoins?: number | null;
  timeLimitedHours?: number | null;
}

const GameChallenges = () => {
  const toast = useToast();
  document.title = "Gamelight - Game Challenges";
  const params = useParams<GameChallengesRouteParams>();
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [challenges, setChallenges] = useState<GameChallengesType[]>([]);
  const [originalChallenges, setOriginalChallenges] = useState<
    GameChallengesType[]
  >([]);

  const { ConfirmDialog, openDialog } = useConfirmDialog({
    destructive: true,
    title: "Removing Event",
    description:
      "You are about to remove an event. This action is not revertable.",
  });

  const fetchGameChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Post("/manage/partner/app-challenge", {
        partnerId: Number(params.partnerId),
        appId: params.appId,
      });

      const updatedChallenges = response.map((_challenge: any) => ({
        ..._challenge,
        originalTitle: _challenge.originalTitle,
        isActive: _challenge.isActive,
      }));

      setOriginalChallenges(updatedChallenges);
      setChallenges(updatedChallenges);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }, [params.appId, params.partnerId]);

  const addNewChallengeItem = useCallback(() => {
    setChallenges((_challenges) => [
      ..._challenges,
      {
        eventName: "",
        coins: "",
        showAsValue: false,
        appChallengeId: null,
        originalTitle: "",
        originalUnit: "",
        threshold: "1",
        isActive: true,
        timeCoins: null,
        timeLimitedHours: null,
      },
    ]);
  }, []);

  const handleChallengesRemove = useCallback(
    async (index: number) => {
      const result = await openDialog();

      if (!result) return;

      const challenge = challenges[index];

      if (challenge.appChallengeId) {
        const response = await Delete(
          `/manage/partner/app-challenge/${challenge.appChallengeId}`
        );

        if (response?.err) {
          toast({
            title: "Error",
            description: response.err,
            status: "error",
            duration: 3000,
            isClosable: true,
          });

          return;
        }

        toast({
          title: "Success",
          description: "Event Removed",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      setChallenges((_challenges) => [
        ..._challenges.filter((_, idx) => idx !== index),
      ]);
    },
    [openDialog, challenges, toast]
  );

  const handleChallengesFieldChange = useCallback(
    (value: string, index: number, type: string) => {
      if (type === "coins" && parseInt(value, 10) < 0) return;
      if (type === "timeCoins" && parseInt(value, 10) < 0) return;
      if (type === "timeLimitedHours" && parseInt(value, 10) < 0) return;

      setChallenges((_challenges) => [
        ..._challenges.map((challenge, idx) => {
          return index === idx
            ? {
                appChallengeId: challenge.appChallengeId,
                eventName: type === "eventName" ? value : challenge.eventName,
                coins: type === "coins" ? value : challenge.coins,
                showAsValue:
                  type === "showAsValue"
                    ? Boolean(value === "true")
                    : challenge.showAsValue,
                originalTitle:
                  type === "originalTitle" ? value : challenge.originalTitle,
                originalUnit:
                  type === "originalUnit" ? value : challenge.originalUnit,
                threshold:
                  type === "threshold"
                    ? !challenge.originalUnit
                      ? "1"
                      : value
                    : challenge.threshold,
                isActive:
                  type === "isActive"
                    ? Boolean(value === "true")
                    : challenge.isActive,
                timeCoins:
                  type === "timeCoins" ? Number(value) : challenge.timeCoins,
                timeLimitedHours:
                  type === "timeLimitedHours"
                    ? Number(value)
                    : challenge.timeLimitedHours,
              }
            : challenge;
        }),
      ]);

      if (type === "threshold" && !challenges[index].originalUnit) {
        toast({
          title: "Error",
          description:
            'Threshold value can be only " 1 " when original unit is empty',
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      if (type === "originalUnit" && !value) {
        setChallenges((_challenges) => [
          ..._challenges.map((challenge, idx) =>
            index === idx ? { ...challenge, threshold: "1" } : challenge
          ),
        ]);
      }
    },
    [challenges, toast]
  );

  const handleChallengesSave = async () => {
    setProcessing(true);
    try {
      const response = await Post("/manage/partner/app-challenge-update", {
        challenges: challenges.map((challenge) => ({
          ...challenge,
          coins: Number(challenge.coins),
          originalUnit: challenge.originalUnit
            ? challenge.originalUnit.toString()
            : null,
          threshold: Number(challenge.threshold),
          timeCoins: challenge.timeCoins ? Number(challenge.timeCoins) : null,
          timeLimitedHours: challenge.timeLimitedHours
            ? Number(challenge.timeLimitedHours)
            : null,
          appid: params.appId,
          partnerId: Number(params.partnerId),
        })),
      });
      await fetchGameChallenges();
      if (response?.err) {
        toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description: "Game Challenges Updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    fetchGameChallenges();
  }, [fetchGameChallenges]);

  return (
    <Flex
      flex={1}
      pt={NAVBAR_HEIGHT}
      minH={"90vh"}
      alignItems="flex-start"
      justifyContent={"space-between"}
    >
      <Flex flexDir={"column"}>
        <MultiInputEntries
          width={"100%"}
          sectionTitle="Game Challenges"
          handleFieldChange={handleChallengesFieldChange}
          addNewItem={addNewChallengeItem}
          handleSave={handleChallengesSave}
          items={challenges}
          originalItems={originalChallenges}
          loading={loading}
          processing={processing}
          disabledKey="isActive"
          setItems={setChallenges}
          onRemove={handleChallengesRemove}
          inputFields={[
            {
              title: "Event Name *",
              inputType: "text",
              keyName: "eventName",
              placeHolder: "Name",
              charLimit: 60,
              columnSize: 4,
            },
            {
              title: "Event Title *",
              inputType: "text",
              keyName: "originalTitle",
              placeHolder: "Title",
              charLimit: 60,
              columnSize: 4,
            },
            {
              title: "Coins *",
              inputType: "numeric",
              keyName: "coins",
              placeHolder: "Coins",
              charLimit: 26,
              columnSize: 2,
            },
            {
              title: "Original Unit",
              inputType: "text",
              keyName: "originalUnit",
              placeHolder: "<empty>",
              charLimit: 26,
              nullable: true,
              columnSize: 3,
            },
            {
              title: "Threshold *",
              inputType: "numeric",
              keyName: "threshold",
              placeHolder: "Threshold",
              charLimit: 26,
              columnSize: 2,
            },
            {
              title: "Time Coins",
              inputType: "numeric",
              keyName: "timeCoins",
              placeHolder: "Time Coins",
              charLimit: 26,
              nullable: true,
              columnSize: 2,
            },
            {
              title: "Time Limited Hours",
              inputType: "numeric",
              keyName: "timeLimitedHours",
              placeHolder: "Hours",
              charLimit: 26,
              nullable: true,
              columnSize: 2,
            },
            {
              title: "Show As Value",
              inputType: "checkbox",
              keyName: "showAsValue",
              placeHolder: "Show As Value",
              nullable: true,
              columnSize: 2,
            },
            {
              title: "Activated",
              inputType: "checkbox",
              keyName: "isActive",
              placeHolder: "Activated",
              nullable: true,
              columnSize: 2,
            },
          ]}
          defaultEditableValue={false}
        />
      </Flex>
      {ConfirmDialog}
    </Flex>
  );
};

export default GameChallenges;
